import { OkResponse, SessionResponse, type User } from '@mtg/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api.ts';

export type AuthState = { status: 'loading' } | { status: 'signedOut' } | { status: 'signedIn'; user: User };

type AuthContextValue = {
  state: AuthState;
  /** After a passkey sign-in or sign-up. */
  setUser: (user: User) => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    api('/api/auth/session', SessionResponse)
      .then(({ user }) => setState(user ? { status: 'signedIn', user } : { status: 'signedOut' }))
      .catch(() => setState({ status: 'signedOut' }));
  }, []);

  const setUser = useCallback((user: User) => setState({ status: 'signedIn', user }), []);

  const signOut = useCallback(async () => {
    await api('/api/auth/logout', OkResponse, { body: {} }).catch(() => {});
    setState({ status: 'signedOut' });
  }, []);

  const value = useMemo(() => ({ state, setUser, signOut }), [state, setUser, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
